import { useRef, useState } from "react";
import { PrimaryButton } from "../../components/Button/Buttons";
import AIModelDropdown from "../../components/Dropdowns/AIModelDropdown";
import Input from "../../components/Inputs/Input";
import TextArea, { TextAreaWithCharacterLimit } from "../../components/Inputs/TextArea";
import { FormLabel } from "../../components/Labels/labels";
import FileUploadInSpectraCreation from "../../components/Spectra/FileUploadInSpectraCreation";
import AlertBanner from "../../components/shared/AlertBanner/AlertBanner";
import Toggle from "../../components/shared/Toggle";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useCreateSpectraMutation, useGetAIModels } from "../../api/queryHooks";
import useModelPreferences from "../../hooks/useModelPreferences";
import { v4 as uuidv4 } from "uuid";
import UserAuthStore from "../../store/userStore";
import { useNavigate } from "react-router-dom";
import EmojiPickerComponent from "../../components/EmojiPicker/EmojiPicker";
import { ScreenSize } from "../../constants/const";
import ROUTES from "../../router/routes";
import Header from "../../components/Header/Header";
import Loader from "../../components/Loader/Loader";
import spaceStore from "../../store/spaceStore";
import logger from "../../services/Logger";
import { SpectraUploadStore } from "../../store/fileUploadStore";

const CreateSpectraPage = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .trim()
      .min(1, "Title is required")
      .required("Title is required"),
    tag: Yup.string()
      .matches(/^#[a-z][a-z-]*$/, "Tag must start with # followed by at least one letter")
      .required("Tag is required"),
    aiModel: Yup.object().required("AI Model is required"),
  });

  const { isPending: isLoading, mutate } = useCreateSpectraMutation({
    onSuccess: (data, variables) => {
      SpectraUploadStore.getState().start({
        spaceId: data.space_id || variables.sid,
        files,
      }).catch((uploadError) => {
        logger.error('CreateSpectra', 'Background upload failed', {
          error: uploadError.message,
          spaceId: variables.sid,
        });
      });
      spaceStore.getState().setOpenSpectraId(null);
      navigate(ROUTES.SPECTRA.ROOT);
    },
    onError: (error) => {
      logger.error('CreateSpectra', 'Mutation onError called', { error: error.message });
      setError(
        error.response?.data?.error
        || error.response?.data?.detail
        || "Failed to create Spectra. Please try again."
      );
    },
  });

  const { getEnabledModels } = useModelPreferences();
  const { data: allModels, isLoading: isLoadingModels, isError } = useGetAIModels();
  const options = getEnabledModels(allModels);

  const createNewSpectra = async (values) => {
    setError(null);
    const { user, orgId } = UserAuthStore.getState();
    const sid = uuidv4();
    const payload = {
      sid,
      title: values.title?.trim(),
      tag: values.tag?.trim(),
      emoji: values.emoji,
      description: values.description?.trim(),
      org_id: orgId,
      uid: user.uid,
      model_id: values.aiModel.id,
      files: [],
      system_prompt: values.systemInstructions?.trim(),
      ...(values.webSearchEnabled === false && { web_search_enabled: false }),
      uploads: files.map(({ file, uploadId }) => ({
        upload_id: uploadId,
        filename: file.name,
        mime_type: file.type || "application/octet-stream",
        size_bytes: file.size,
      })),
    };

    mutate(payload);
  };

  const formik = useFormik({
    initialValues: {
      title: "",
      tag: "#",
      emoji: "",
      description: "",
      aiModel: options?.find((model) => model?.is_default) || options?.[0],
      webSearchEnabled: true,
      systemInstructions: "",
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: createNewSpectra,
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    setFieldValue,
    handleBlur,
    submitCount,
  } = formik;

  // Custom handler for tag to transform input
  const handleTagChange = (e) => {
    const suffix = e.target.value
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/[^a-z-]/g, '');

    setFieldValue('tag', `#${suffix}`);
  };

  const isDisabled = isLoading;

  const getButtonText = () => {
    if (isLoading) return "Creating Spectra";
    return "Create Spectra";
  };

  if (isLoadingModels || isError) {
    return (
      <div className="w-full h-screen flex items-center justify-center dark:bg-surface-page bg-surface-page">
        <Loader variant="form" />
      </div>
    );
  }

  return (
    <div className="w-full">
      <Header type="spectra-dashboard" />
      <div className="h-[calc(100vh-80px)] overflow-auto show-scrollbar">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
          {/* Header Section */}
          <div className="bg-surface-panel dark:bg-surface-panel rounded-2xl shadow-sm p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-xl font-medium text-ink-ink dark:text-ink-white">
                  Create Spectra
                </h1>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate(ROUTES.SPECTRA.ROOT)}
                  className="px-4 py-2 text-sm text-ink-gray-600 dark:text-ink-gray-300 hover:text-ink-gray-800 dark:hover:text-ink-white border border-edge-gray-300 dark:border-edge-gray-600 rounded-lg hover:bg-fill-gray-50 dark:hover:bg-fill-gray-800 transition-colors w-full sm:w-auto"
                >
                  Cancel
                </button>
                <PrimaryButton
                  type="submit"
                  form="create-spectra-form"
                  disabled={isDisabled || isLoading}
                  className="px-6 py-2 w-full sm:w-auto"
                >
                  {getButtonText()}
                </PrimaryButton>
              </div>
            </div>
          </div>

          {/* Form Section */}
          <div className="bg-surface-panel dark:bg-surface-panel rounded-2xl shadow-sm p-6">
            <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
              {/* Left Column - Form Fields */}
              <div className="flex-1 lg:max-w-2xl">
                <form id="create-spectra-form" onSubmit={handleSubmit}>
                  <div className="space-y-6">
                    {/* Title and Emoji Row */}
                    <div className="flex flex-col sm:flex-row items-start gap-4">
                      <div className="flex-1 w-full">
                        <FormLabel htmlFor="create-spectra-title">Title <span className="text-ink-red-500">*</span></FormLabel>
                        <Input
                          id="create-spectra-title"
                          name="title"
                          placeholder="E.g. LinkedIn Post Generator"
                          value={values.title}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`mt-2 !border-edge-gray-200 dark:!border-edge-gray-600 ${errors.title && (touched.title || submitCount > 0) ? "!border-edge-red-500" : ""}`}
                          autoFocus
                        />
                        {errors.title && (touched.title || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.title}</p>
                        )}
                      </div>
                      <div className="flex flex-col sm:items-center">
                        <FormLabel htmlFor="create-spectra-emoji" className="mb-2">Emoji</FormLabel>
                        <EmojiPickerComponent
                          id="create-spectra-emoji"
                          onEmojiSelect={(emoji) => setFieldValue("emoji", emoji)}
                          selectedEmoji={values.emoji}
                        />
                      </div>
                    </div>

                    {/* AI Model */}
                    <div>
                      <FormLabel htmlFor="create-spectra-ai-model">AI Model <span className="text-ink-red-500">*</span></FormLabel>
                      <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400 mb-2">
                        Assign a specific model to this AI Agent
                      </p>
                      <div className="w-full sm:max-w-xs">
                        <AIModelDropdown
                          id="create-spectra-ai-model"
                          containerRef={containerRef}
                          selectedModel={values.aiModel}
                          onSelect={(model) => setFieldValue("aiModel", model)}
                          className={`!border-edge-gray-200 dark:!border-edge-gray-600 ${errors.aiModel && (touched.aiModel || submitCount > 0) ? "!border-edge-red-500" : ""}`}
                        />
                        {errors.aiModel && (touched.aiModel || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">AI Model is required</p>
                        )}
                      </div>
                    </div>

                    {/* Tag Name */}
                    <div>
                      <FormLabel htmlFor="create-spectra-tag">Tag <span className="text-ink-red-500">*</span></FormLabel>
                      <div className="relative mt-2 w-full sm:max-w-xs">
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute inset-y-0 left-[10px] z-10 flex items-center text-[13px] text-ink-ink dark:text-ink-white"
                        >
                          #
                        </span>
                        <Input
                          id="create-spectra-tag"
                          name="tag"
                          placeholder="tag"
                          value={values.tag.slice(1)}
                          onChange={handleTagChange}
                          onBlur={handleBlur}
                          className={`!pl-5 !border-edge-gray-200 dark:!border-edge-gray-600 ${
                            errors.tag && (touched.tag || submitCount > 0) ? "!border-edge-red-500" : ""
                          }`}
                        />
                      </div>
                      {errors.tag && (touched.tag || submitCount > 0) && (
                        <p className="text-ink-red-500 text-xs mt-1">{errors.tag}</p>
                      )}
                    </div>

                    {/* Description */}
                    <div>
                      <FormLabel htmlFor="create-spectra-description" isOptional>Description</FormLabel>
                      <TextArea
                        id="create-spectra-description"
                        name="description"
                        className="block w-full mt-2 h-[120px] resize-none !border-edge-gray-200 dark:!border-edge-gray-600"
                        placeholder="Describe what the AI agent does for the user."
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </div>

                    {/* System Instructions */}
                    <div>
                      <FormLabel htmlFor="create-spectra-system-instructions" isOptional>System Instructions</FormLabel>
                      <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400 mb-2">
                        Instructions to the AI that affects every prompt for this agent.
                      </p>
                      <TextAreaWithCharacterLimit
                        id="create-spectra-system-instructions"
                        name="systemInstructions"
                        placeholder="Enter keywords..."
                        maxLength={ScreenSize}
                        value={values.systemInstructions}
                        onChange={(e) =>
                          setFieldValue("systemInstructions", e.target.value)
                        }
                        onBlur={handleBlur}
                        resizable={true}
                        minHeight="150px"
                        className="!border-edge-gray-200 dark:!border-edge-gray-600"
                      />
                    </div>
  {/* Web Search Toggle */}
                    <div className="flex items-center justify-between gap-4 pt-2 border-t border-edge-gray-200 dark:border-edge-gray-700">
                      <div>
                        <FormLabel htmlFor="create-spectra-web-search">Web Search</FormLabel>
                        <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400">
                          Allow AI in this Spectra to search the web and fetch external URLs.
                        </p>
                      </div>
                      <Toggle
                        checked={values.webSearchEnabled}
                        onChange={(checked) => setFieldValue("webSearchEnabled", checked)}
                      />
                    </div>

                  
                  </div>


                  {error && (
                    <AlertBanner className="mt-6">
                      {error}
                    </AlertBanner>
                  )}
                </form>
              </div>

              {/* Right Column - Add Attachments */}
              <div className="w-full lg:w-80">
                <div className="bg-surface-muted-panel dark:bg-surface-muted-panel rounded-xl p-4 md:p-6">
                  <FileUploadInSpectraCreation files={files} setFiles={setFiles} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateSpectraPage;
